/**
 * Словарь показателей. Спор о том, что значит число на экране, должен
 * решаться ссылкой, а не перепиской.
 *
 * Поиск здесь не украшение: показателей десятки, а приходят сюда всегда за
 * одним конкретным — и листать до него значит не найти.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type MetricDef } from "../lib/api";
import { Badge, Card, Empty, ErrorBox, Loading, PageHeader } from "../components/ui";

export default function MethodologyPage() {
  const [search, setSearch] = useState("");

  const query = useQuery({
    queryKey: ["metrics"],
    queryFn: () => api.get<{ metrics: MetricDef[] }>("/metrics/catalog"),
  });

  const metrics = query.data?.metrics ?? [];
  const found = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return metrics;
    return metrics.filter((metric) =>
      [metric.title, metric.key, metric.definition, metric.calculation, metric.source]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [metrics, search]);

  if (query.isLoading) return <Loading rows={6} />;
  if (query.error) return <ErrorBox error={query.error} />;

  return (
    <>
      <PageHeader
        title="Как считаются числа"
        lede="У каждого показателя здесь есть определение, источник, формула, выборка и то, чему верить нельзя. Почти всякое «в отчёте цифры не сходятся» — это два места, называющие одним словом разные выборки."
        actions={
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Найти показатель"
            aria-label="Поиск по показателям"
            style={{ width: 240 }}
          />
        }
      />

      {found.length === 0 ? (
        <Card>
          <Empty title="Ничего не нашлось">
            По запросу «{search}» показателей нет. Попробуйте часть слова — поиск идёт и по
            определению, и по формуле.
          </Empty>
        </Card>
      ) : (
        found.map((metric) => (
          <Card
            key={metric.key}
            title={metric.title}
            note={metric.unit}
            actions={
              metric.higher_is_better !== null && (
                <Badge tone="muted">
                  {metric.higher_is_better ? "больше — лучше" : "меньше — лучше"}
                </Badge>
              )
            }
          >
            <div className="table-wrap">
              <table className="plain">
                <tbody>
                  <tr>
                    <td className="dim" style={{ width: 180 }}>
                      Что это
                    </td>
                    <td>{metric.definition}</td>
                  </tr>
                  <tr>
                    <td className="dim">Как считается</td>
                    <td>{metric.calculation}</td>
                  </tr>
                  <tr>
                    <td className="dim">По какой выборке</td>
                    <td>{metric.population}</td>
                  </tr>
                  <tr>
                    <td className="dim">Чему верить нельзя</td>
                    <td>{metric.limitations}</td>
                  </tr>
                  <tr>
                    <td className="dim">Источник</td>
                    <td className="mono">{metric.source}</td>
                  </tr>
                  {metric.platforms.length > 0 && (
                    <tr>
                      <td className="dim">Площадки</td>
                      <td>
                        <span className="pill-row">
                          {metric.platforms.map((platform) => (
                            <Badge key={platform} tone="muted">
                              {platform}
                            </Badge>
                          ))}
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        ))
      )}
    </>
  );
}
